import { useCallback } from 'react';
import Vec2 from 'vec2';

import { UserDesignOperation } from '~/core/enums';
import { DrawingTabEnum } from '~/core/enums/drawing-tab.enum';
import { updateShapeByTranslation } from '~/core/helpers';
import useAppliance from '~/core/hooks/useAppliance';
import { useRegisterUserAction } from '~/core/hooks/useRegisterUserAction';
import { DrawingShape } from '~/core/models';
import {
  useDrawingShapeContext,
  useFabricCanvasContext,
} from '~/legacy/easy-estimator-page/context';
import { useAppDispatch } from '~/redux/hooks';
import {
  setPreventRepricing,
  updateNeedRedrawCanvas,
} from '~/redux/slices/drawingShape';
import { updateSelectedDrawingTab } from '~/redux/slices/shape';

export const useMouseUp = () => {
  const dispatch = useAppDispatch();

  const {
    getters: { numberOfShapesInTheDrawingArea },
    handlers: { updateDrawingShapes },
  } = useDrawingShapeContext();

  const { drawingShapesRef, movingItemRef } = useFabricCanvasContext();

  const { updateApplianceByTranslation } = useAppliance();
  const { registerUserAction } = useRegisterUserAction();

  const handleShapeMovement = useCallback(
    (moveVec: Vec2) => {
      if (numberOfShapesInTheDrawingArea > 1) {
        registerUserAction(UserDesignOperation.MOVE_SHAPE);
      }
      dispatch(setPreventRepricing(true));
      const shape = movingItemRef.current as DrawingShape;

      updateShapeByTranslation(moveVec, shape);
      updateDrawingShapes([...drawingShapesRef.current]);
    },
    [
      dispatch,
      drawingShapesRef,
      movingItemRef,
      numberOfShapesInTheDrawingArea,
      registerUserAction,
      updateDrawingShapes,
    ]
  );

  const handleApplianceMovement = useCallback(
    (moveVec: Vec2) => {
      // moving an already selected appliance
      dispatch(setPreventRepricing(true));
      updateApplianceByTranslation(
        moveVec,
        movingItemRef.current as string,
        drawingShapesRef.current
      );
      updateDrawingShapes([...drawingShapesRef.current]);
      registerUserAction(UserDesignOperation.MOVE_APPLIANCE);
    },
    [
      dispatch,
      drawingShapesRef,
      movingItemRef,
      registerUserAction,
      updateApplianceByTranslation,
      updateDrawingShapes,
    ]
  );

  const finalizeDraggingState = useCallback(() => {
    dispatch(updateSelectedDrawingTab(DrawingTabEnum.SELECTED_SHAPE_TAB));
    dispatch(updateNeedRedrawCanvas(true));
    movingItemRef.current = undefined;
  }, [dispatch, movingItemRef]);

  return {
    finalizeDraggingState,
    handleShapeMovement,
    handleApplianceMovement,
  };
};
